import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { statusService } from '@/services/statusService';
import { sessionService } from '@/services/sessionService';
import { useProfileQuery, Profile } from '@/hooks/useProfileQuery';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  isInitialized: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  isInitialized: false,
  signOut: async () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const queryClient = useQueryClient();
  const recoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recoveryInFlightRef = useRef(false);

  // Handle logout events from other devices
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = sessionService.onLogout(async (event) => {
      // If this logout event is for our user and not from our session, sign out
      if (event.userId === user.id) {
        console.log('Logout event received from another device, signing out...');

        // First set status to offline - try multiple approaches
        try {
          await statusService.updateStatus('offline', false, true, user.id);
        } catch (error) {
          console.error('Error setting offline status on logout:', error);
          // Fallback: direct database update
          try {
            await supabase
              .from('profiles')
              .update({ status: 'offline', updated_at: new Date().toISOString() })
              .eq('id', user.id);
            console.log('Direct database update: set user offline on logout event');
          } catch (directError) {
            console.error('Direct database update failed on logout:', directError);
          }
        }

        // Force sign out without triggering another logout broadcast
        await statusService.cleanup();
        await supabase.auth.signOut();
      }
    });

    return unsubscribe;
  }, [user?.id]);

  // Use React Query for robust profile fetching
  const { data: profile } = useProfileQuery(user?.id);

  // Background profile data fetching for UI updates
  useEffect(() => {
    if (!user?.id) return;

    let wasDisconnected = false;
    let healthCheckInterval: NodeJS.Timeout | null = null;
    let lastSuccessfulCheck = Date.now();

    // Function to fetch profile data directly (same as Profile.tsx does)
    const fetchProfileData = async () => {
      try {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, email, created_at, avatar_url, avatar_path, job_title, bio, role, settings')
          .in('id', [user.id])
          .single();

        if (profileError) {
          console.error('Profile fetch error:', profileError);
          throw profileError;
        }

        // Update the React Query cache with fresh data
        queryClient.setQueryData(['userProfile', user.id], profileData);

        return profileData;
      } catch (error) {
        console.error('Error fetching profile data:', error);
        throw error;
      }
    };

    // Function to check Supabase connectivity and fetch profile data
    const checkConnectivityAndFetch = async () => {
      try {
        // First check connectivity
        const { data, error } = await supabase.auth.getSession();

        if (!error && data.session) {
          const now = Date.now();
          // If we previously detected a disconnection and now we're connected
          if (wasDisconnected && (now - lastSuccessfulCheck) > 5000) { // More than 5 seconds since last success
            await fetchProfileData();
            wasDisconnected = false;
          } else if (!wasDisconnected) {
            // Regular fetch every 5 seconds when connected
            await fetchProfileData();
          }
          lastSuccessfulCheck = now;
        } else {
          // Connection issue detected
          if (!wasDisconnected) {
            wasDisconnected = true;
          }
        }
      } catch (error) {
        // Connection failed
        if (!wasDisconnected) {
          wasDisconnected = true;
        }
        console.error('Connectivity check failed:', error);
      }
    };

    // Check connection immediately and then every 5 seconds
    checkConnectivityAndFetch();
    healthCheckInterval = setInterval(checkConnectivityAndFetch, 5000);

    // Also trigger checks on visibility changes and window focus
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible - fetch immediately
        setTimeout(checkConnectivityAndFetch, 500);
      }
    };

    const handleFocus = () => {
      // Window gained focus - fetch immediately
      setTimeout(checkConnectivityAndFetch, 200);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
      }
    };
  }, [user?.id, queryClient]);

  // When the tab resumes from a long suspension or connectivity returns, re-sync auth
  // and refetch active queries so the UI repopulates without a hard refresh.
  useEffect(() => {
    if (!isInitialized) return;

    let mounted = true;

    const syncSessionAndQueries = async () => {
      if (recoveryInFlightRef.current) return;

      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return;
      }

      recoveryInFlightRef.current = true;

      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!mounted) return;

        const nextUser = session?.user ?? null;
        const currentUserId = nextUser?.id ?? null;

        // Keep auth state aligned with Supabase so mounted queries can re-enable.
        setUser(nextUser);

        if (currentUserId) {
          await queryClient.refetchQueries({ type: 'active' });
        }
      } catch (error) {
        console.error('Error resyncing auth and query state:', error);
      } finally {
        if (mounted) {
          recoveryInFlightRef.current = false;
        }
      }
    };

    const scheduleRecoverySync = () => {
      if (recoveryTimerRef.current) {
        clearTimeout(recoveryTimerRef.current);
      }

      recoveryTimerRef.current = setTimeout(() => {
        void syncSessionAndQueries();
      }, 250);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        scheduleRecoverySync();
      }
    };

    const handleFocus = () => {
      scheduleRecoverySync();
    };

    const handleOnline = () => {
      scheduleRecoverySync();
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        scheduleRecoverySync();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);
    window.addEventListener('pageshow', handlePageShow);

    // Run once after initialization so a restored page can self-heal immediately.
    scheduleRecoverySync();

    return () => {
      mounted = false;

      if (recoveryTimerRef.current) {
        clearTimeout(recoveryTimerRef.current);
        recoveryTimerRef.current = null;
      }

      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [isInitialized, queryClient]);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // Check active sessions and sets the user
        const { data: { session } } = await supabase.auth.getSession();

        if (mounted) {
          // Check if user has confirmed their email
          const user = session?.user;
          if (user && !user.email_confirmed_at) {
            // Email not confirmed - don't set the user
            setUser(null);
            setLoading(false);
            setIsInitialized(true);
            console.log('User has unconfirmed email during initialization');
            return;
          }

          setUser(user ?? null);
          setLoading(false);
          setIsInitialized(true);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (mounted) {
          setLoading(false);
          setIsInitialized(true);
        }
      }
    };

    initializeAuth();

    // Listen for changes on auth state (logged in, signed out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (mounted) {
        // Check if user has confirmed their email
        const user = session?.user;
        if (user && !user.email_confirmed_at) {
          // Email not confirmed - don't set the user and show error
          setUser(null);
          setLoading(false);
          setIsInitialized(true);
          console.log('User attempted to sign in without confirming email');
          return;
        }

        setUser(user ?? null);
        setLoading(false);
        setIsInitialized(true);

        // Status restoration is now handled by useOnlineStatus hook
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    if (user?.id) {
      // Store current status as preferred before logout
      try {
        const currentStatusInfo = await statusService.getUserStatus(user.id);
        if (currentStatusInfo && currentStatusInfo.status !== 'offline') {
          // Save to database instead of localStorage
          await statusService.savePreferredStatus(user.id, currentStatusInfo.status);
        }
      } catch (error) {
        console.error('Error saving preferred status:', error);
      }

      // Set status to offline - use keepalive to ensure it completes
      try {
        await statusService.updateStatus('offline', true, true, user.id);
        console.log('Set user offline during logout');
      } catch (error) {
        console.error('Error setting offline status during logout:', error);
        // Try one more time with a direct database update
        try {
          await supabase
            .from('profiles')
            .update({ status: 'offline', updated_at: new Date().toISOString() })
            .eq('id', user.id);
          console.log('Direct database update: set user offline');
        } catch (directError) {
          console.error('Direct database update failed:', directError);
        }
      }

      // Delete all sessions for this user (logout from all devices)
      try {
        await sessionService.deleteAllSessions(user.id);
        console.log('Deleted all sessions for user');
      } catch (error) {
        console.error('Error deleting sessions:', error);
      }
    }

    // Clean up status service (this will also clean up the session)
    await statusService.cleanup();
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, isInitialized, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
}; 
